import { Pageable } from '@/types/Pageable'
import { HTTPGatewayClient } from './http-client'
import { Category, entityToCategory } from '@/components/features/assets/category.service'
import { useEventListener } from '@/pinia/events'
import { defineStore } from 'pinia'
import { Ref, ref, watch } from 'vue'

type ComponentDto =
  | {
      type: 'label'
      name: string
      value: string
    }
  | {
      type: 'groupReference'
      groupId: string
    }
  | {
      type: 'path'
      name: string
      path: string
    }
  | {
      type: 'relation'
      relationType: string
      targetId: string
      sourceId: string
    }

type EntityDto = {
  id: string
  createdDate: string
  lastModifiedDate: string
  components?: ComponentDto[]
}

type PageableHateoas<T> = {
  _embedded:
    | {
        [key: string]: T[] | undefined
      }
    | undefined
  page: {
    size: number
    totalElements: number
    totalPages: number
    number: number
  }
}

export type Group = {
  name: string
  color?: string
}

export type Component =
  | {
      type: 'label'
      name: string
      value: string
    }
  | {
      type: 'groupReference'
      groupId: string
    }
  | {
      type: 'path'
      name: string
      path: string
    }
  | {
      type: 'point'
      geometry: {
        type: 'Point'
        coordinates: [number, number]
      }
      height: number
    }
  | {
      type: 'relation'
      relationType: string
      targetId: string
      sourceId: string
    }

export type Entity = {
  id: string
  type?: string
  name: string
  displayName: string
  createdDate?: Date
  mainPath?: string
  parentId?: string
  lastModifiedDate?: Date
  labels?: Record<string, string>
  components: Component[]
  categories: Category[]
}

export type RelationCounts = {
  /** Number of entities matching the search. */
  total: number
  /** Per relation target id, how many of the matching entities have that relation to it. */
  counts: Record<string, number>
}

export type EntityOptions = {
  includeCategories?: boolean
  includeRelated?: string[]
}

const CACHE_TTL_MS = 5 * 60 * 1000

type CacheEntry<T> = {
  promise: Promise<T>
  expiry: number
  controller: AbortController
  refCount: number
}

const entityCache = new Map<string, CacheEntry<any>>()

const createStableKey = (obj: any): string => {
  if (obj === null || typeof obj !== 'object') {
    return JSON.stringify(obj)
  }
  if (Array.isArray(obj)) {
    return `[${obj.map(createStableKey).join(',')}]`
  }
  const sortedKeys = Object.keys(obj).sort((a, b) => a.localeCompare(b))
  const keyValuePairs = sortedKeys.map(
    (key) => `${JSON.stringify(key)}:${createStableKey(obj[key])}`
  )
  return `{${keyValuePairs.join(',')}}`
}

/**
 * A generic caching wrapper that caches the in-flight promise to prevent race conditions.
 */
async function withCache<T>(
  key: string,
  // Fetcher now receives a signal to pass to axios
  fetcher: (signal: AbortSignal) => Promise<T>,
  callerSignal?: AbortSignal,
  ttl: number = CACHE_TTL_MS
): Promise<T> {
  let entry = entityCache.get(key)

  // 1. If no valid entry, create one
  if (!entry || Date.now() >= entry.expiry) {
    const controller = new AbortController()

    // Create the promise using the master controller's signal
    const promise = fetcher(controller.signal).catch((err) => {
      // If network fails, remove from cache immediately
      if (entityCache.get(key)?.promise === promise) {
        entityCache.delete(key)
      }

      throw err
    })

    entry = {
      promise,
      expiry: Date.now() + ttl,
      controller,
      refCount: 0
    }
    entityCache.set(key, entry)
  }

  // 2. Increment Reference Count
  entry.refCount++

  // 3. Handle Caller Cancellation
  if (callerSignal) {
    if (callerSignal.aborted) {
      entry.refCount--
      if (entry.refCount <= 0) {
        entry.controller.abort()
        entityCache.delete(key)
      }
      throw new Error('Aborted') // Or specific AbortError
    }

    // Listen for future aborts
    const abortHandler = () => {
      if (!entry) return
      entry.refCount--

      // If nobody is listening anymore, kill the network request
      if (entry.refCount <= 0) {
        entry.controller.abort()
        entityCache.delete(key)
      }
    }

    callerSignal.addEventListener('abort', abortHandler, { once: true })

    // Cleanup listener once the request settles. Both handlers, so a failed request
    // doesn't leave a rejected promise behind unhandled (the caller handles its own).
    const removeAbortHandler = () => callerSignal.removeEventListener('abort', abortHandler)
    entry.promise.then(removeAbortHandler, removeAbortHandler)
  }

  // 4. Return the shared promise
  // We wrap it in a race so the caller rejects immediately if *their* signal fires,
  // even if the network request is still running for other people.
  return new Promise<T>((resolve, reject) => {
    if (callerSignal?.aborted) return reject(new Error('Canceled'))

    const onAbort = () => reject(new Error('Canceled'))
    callerSignal?.addEventListener('abort', onAbort, { once: true })

    entry.promise
      .then(resolve)
      .catch(reject)
      .finally(() => {
        callerSignal?.removeEventListener('abort', onAbort)
      })
  })
}

// `dtoToEntity` only reads `includeCategories` from EntityOptions (includeRelated only
// affects getAll()'s backend query, never the entity shape), so that's the only option
// that can change a `get()` result — normalize on it alone rather than the raw options
// object, or callers passing `{}` vs `{ includeCategories: false }` vs an options object
// that also carries `includeRelated` would each get their own cache entry for identical data.
function entityCacheKey(id: string, options: EntityOptions = {}): string {
  return `entity:${id}:${!!options.includeCategories}`
}

// Seeds the per-id `get()` cache from an entity already fetched via `getAll()`, so a
// subsequent `get(id, { includeCategories })` for the same id (e.g. selecting an asset
// that's already loaded on the map) is a cache hit instead of a fresh request.
function seedEntityCache(entity: Entity, includeCategories: boolean) {
  const cacheKey = entityCacheKey(entity.id, { includeCategories })
  if (entityCache.has(cacheKey)) return
  entityCache.set(cacheKey, {
    promise: Promise.resolve(entity),
    expiry: Date.now() + CACHE_TTL_MS,
    controller: new AbortController(),
    refCount: 0
  })
}

function updateDisplayName(entity: Entity) {
  const name = entity.name
  const categories = entity.categories
  entity.displayName = name?.trim()?.length ? name : categories.map((c) => c.name).join(' / ')
}

async function getParents(paths: string[], pathName: string = 'main'): Promise<Entity[]> {
  const uniqueSortedPaths = Array.from(new Set(paths)).sort((a, b) => a.localeCompare(b))
  if (uniqueSortedPaths.length === 0) {
    return []
  }

  const cacheKey = `parents:${pathName}:${createStableKey(uniqueSortedPaths)}`

  return withCache(cacheKey, async () => {
    const all = await getAll({
      page: 0,
      size: 1000,
      'labels.type': 'CATEGORY',
      [`path.${pathName}.or`]: uniqueSortedPaths.map((path) => `ancestorOf:${path}`)
    })
    return all.content
  })
}

async function getCategoryChainById(categoryId: string): Promise<Category[]> {
  const cacheKey = `catChain:${categoryId}`
  return withCache(cacheKey, async () => {
    const catEntity = await get(categoryId)
    if (!catEntity) return []
    const catPath = catEntity.components.find(
      (c) => c.type === 'path' && c['name'] === 'category'
    )?.['path'] as string | undefined
    if (!catPath) return []
    const parents = await getParents([catPath], 'category')
    return parents
      .map(entityToCategory)
      .toSorted((a, b) => (a.path ?? '').split('.').length - (b.path ?? '').split('.').length)
  })
}

async function dtoToEntity(
  entity: EntityDto,
  { includeCategories }: EntityOptions = {}
): Promise<Entity> {
  const components = Object.values(entity.components ?? {})
  const lastModifiedDate = components.reduce((latest, component) => {
    if ('lastModifiedDate' in component) {
      const lastModified = component['lastModifiedDate'] as string | undefined
      const componentDate = lastModified ? new Date(lastModified) : new Date(0)
      return componentDate > latest ? componentDate : latest
    }
    return latest
  }, new Date(0))
  const name = components.find((c) => c.type === 'label' && c.name === 'name')?.['value'] ?? ''
  const type = components.find((c) => c.type === 'label' && c.name === 'type')?.['value']
  const id = entity.id
  const mainPath = components.find((c) => c.type === 'path' && c.name === 'main')?.['path'] as
    string | undefined
  const parentId = mainPath?.split('.').slice(-2, -1)[0]

  let categories: Category[] = []
  if (includeCategories) {
    const belongsToId = components.find(
      (c) => c.type === 'relation' && c['relationType'] === 'BELONGS_TO'
    )?.['targetId'] as string | undefined
    if (belongsToId) {
      categories = await getCategoryChainById(belongsToId)
    }
  }

  const labels = components
    .filter((c) => c.type === 'label')
    .reduce<Record<string, string>>((acc, curr) => {
      acc[curr.name] = curr.value
      return acc
    }, {})

  const e: Entity = {
    id,
    type,
    createdDate: new Date(entity.createdDate),
    lastModifiedDate:
      lastModifiedDate > new Date(0) ? lastModifiedDate : new Date(entity.lastModifiedDate),
    name,
    displayName: name,
    parentId: parentId,
    mainPath,
    categories,
    labels,
    components: components.map((c) => ({ ...c, type: c.type }) as Component)
  }

  updateDisplayName(e)
  return e
}

async function get(id: string, options: EntityOptions = {}): Promise<Entity | undefined> {
  const cacheKey = entityCacheKey(id, options)
  return withCache(cacheKey, async () => {
    const resEntity = await HTTPGatewayClient.get<PageableHateoas<EntityDto>>(
      `/entity/entities/search`,
      { params: { id } }
    )
    const entity = resEntity.data._embedded?.['coreEntities']?.[0]
    return entity ? dtoToEntity(entity, options) : undefined
  })
}

export type RelationQuery =
  | {
      source: string
      relationType?: string
    }
  | {
      target: string
      relationType?: string
    }

export function getRelationParams(relationQuery: RelationQuery): Record<string, any> {
  return {
    ...('source' in relationQuery ? { 'relations.source.id': relationQuery.source } : {}),
    ...('target' in relationQuery ? { 'relations.target.id': relationQuery.target } : {}),
    ...('target' in relationQuery && relationQuery.relationType
      ? { 'relations.target.type': relationQuery.relationType }
      : {}),
    ...('source' in relationQuery && relationQuery.relationType
      ? { 'relations.source.type': relationQuery.relationType }
      : {})
  }
}

async function getRelations(
  relationQuery: RelationQuery,
  options: EntityOptions = {}
): Promise<Entity[]> {
  const cacheKey = `references:${createStableKey(relationQuery)}:${createStableKey(options)}`
  return withCache(cacheKey, async () => {
    const res = await HTTPGatewayClient.get<PageableHateoas<EntityDto>>(`/entity/entities/search`, {
      params: {
        ...getRelationParams(relationQuery),
        size: 100
      },
      paramsSerializer: { indexes: null }
    })
    return Promise.all(
      res.data._embedded?.['coreEntities']?.map((entity) => dtoToEntity(entity, options)) ?? []
    )
  })
}

async function getReferences(
  id: string,
  types?: string[],
  options: EntityOptions = {}
): Promise<Entity[]> {
  const cacheKey = `references:${id}:${createStableKey(options)}`
  return withCache(cacheKey, async () => {
    const params = [
      { 'nextTo.id': id, 'labels.type': types, size: 100 },
      { 'path.main.or': `*.${id}.*`, 'labels.type': types, size: 100 }
    ]
    const entities = (
      await Promise.allSettled(
        params.map((params) =>
          HTTPGatewayClient.get<PageableHateoas<EntityDto>>(`/entity/entities/search`, {
            params,
            paramsSerializer: { indexes: null }
          })
        )
      )
    ).flatMap((result) => {
      if (result.status === 'fulfilled') {
        return result.value.data._embedded?.['coreEntities'] ?? []
      }
      return []
    })
    return Promise.all(entities.map((entity) => dtoToEntity(entity, options)))
  })
}

type SearchFilterScalar = string | number | boolean

/** Search filters by param name (e.g. `labels.type`). Null and undefined filters are left out. */
export type SearchFilters = Record<
  string,
  SearchFilterScalar | SearchFilterScalar[] | null | undefined
>

// The POST search endpoints take their filters as a map of value lists.
function toMultiValueMap(params: SearchFilters): Record<string, SearchFilterScalar[]> {
  return Object.keys(params).reduce<Record<string, SearchFilterScalar[]>>((acc, key) => {
    const value = params[key]
    if (value === undefined || value === null) return acc
    return { ...acc, [key]: Array.isArray(value) ? value : [value] }
  }, {})
}

async function getAll(
  {
    size,
    page,
    sort,
    ...rest
  }: { size?: number; page?: number; sort?: string[]; [key: string]: any } = {},
  options: EntityOptions = {},
  config: { signal?: AbortSignal } = {}
): Promise<Pageable<Entity>> {
  const cacheKey = `all:${createStableKey({ size, page, sort, ...rest })}:${createStableKey(
    options
  )}`

  return withCache(
    cacheKey,
    async (internalSignal) => {
      const { includeRelated } = options
      const resEntities = await HTTPGatewayClient.post<PageableHateoas<EntityDto>>(
        `/entity/entities/search`,
        toMultiValueMap(rest),
        {
          params: {
            size,
            page,
            sort,
            ...(includeRelated?.length ? { includeRelated: includeRelated.join(',') } : {})
          },
          paramsSerializer: { indexes: null },
          signal: internalSignal
        }
      )

      const dtoEntities = resEntities.data._embedded?.['coreEntities']
      const entities = dtoEntities
        ? await Promise.all(
            dtoEntities.map((entity) =>
              dtoToEntity(entity, { ...options, includeCategories: false })
            )
          )
        : []

      if (options.includeCategories) {
        const belongsToIds = [
          ...new Set(
            entities
              .map(
                (e) =>
                  (e.components as Component[]).find(
                    (c) => c.type === 'relation' && c['relationType'] === 'BELONGS_TO'
                  )?.['targetId'] as string | undefined
              )
              .filter((id): id is string => Boolean(id))
          )
        ]

        const categoryChainMap = new Map<string, Category[]>()
        await Promise.all(
          belongsToIds.map(async (id) => {
            categoryChainMap.set(id, await getCategoryChainById(id))
          })
        )

        for (const entity of entities) {
          const belongsToId = (entity.components as Component[]).find(
            (c) => c.type === 'relation' && c['relationType'] === 'BELONGS_TO'
          )?.['targetId'] as string | undefined
          if (belongsToId) {
            const chain = categoryChainMap.get(belongsToId)
            if (chain?.length) {
              entity.categories = chain
              updateDisplayName(entity)
            }
          }
        }
      }

      for (const entity of entities) {
        seedEntityCache(entity, !!options.includeCategories)
      }

      return {
        content: entities,
        empty: entities.length === 0,
        first: resEntities.data.page.number === 0,
        last:
          resEntities.data.page.totalPages === 0 ||
          resEntities.data.page.number + 1 >= resEntities.data.page.totalPages,
        number: resEntities.data.page.number,
        numberOfElements: entities.length,
        size: resEntities.data.page.size,
        totalElements: resEntities.data.page.totalElements,
        totalPages: resEntities.data.page.totalPages
      }
    },
    config.signal
  )
}

/**
 * Counts the entities matching the search filters (same as getAll's) per target of their
 * `relationType` relations, e.g. assets per category for BELONGS_TO, in one request.
 */
async function getRelationCounts(
  params: SearchFilters,
  relationType: string
): Promise<RelationCounts> {
  const cacheKey = `relationCounts:${relationType}:${createStableKey(params)}`
  return withCache(cacheKey, async (signal) => {
    const res = await HTTPGatewayClient.post<RelationCounts>(
      `/entity/entities/search/relation-counts`,
      toMultiValueMap(params),
      { params: { relationType }, signal }
    )
    return res.data
  })
}

function clearCache() {
  entityCache.clear()
}

async function searchByParams(
  params: Record<string, string | number>,
  options: EntityOptions = {}
): Promise<Entity[]> {
  const res = await HTTPGatewayClient.get<PageableHateoas<EntityDto>>(`/entity/entities/search`, {
    params
  })
  const entities = res.data._embedded?.['coreEntities'] ?? []
  return Promise.all(entities.map((e) => dtoToEntity(e, options)))
}

async function downloadCSV(
  params: Record<string, any>,
  columns?: string[],
  options?: {
    excludeParams?: string[]
    extraParams?: Record<string, any>
    filename?: string
    ids?: string[]
  }
) {
  const excludeParams = options?.excludeParams ?? ['labels.type']
  const cleanedParams = Object.fromEntries(
    Object.entries(params).filter(([key]) => !excludeParams.includes(key))
  )
  const ids = options?.ids
  const hasCategoryColumns = columns?.some((c) => c.startsWith('CATEGORY.'))
  const hasProductColumns = columns?.some((c) => c.startsWith('PRODUCT.'))
  const includeRelated = [
    ...(hasCategoryColumns ? ['BELONGS_TO'] : []),
    ...(hasProductColumns ? ['IS_TYPE_OF'] : [])
  ].join(',')

  const baseParams = {
    ...cleanedParams,
    ...(options?.extraParams ?? {}),
    ...(includeRelated ? { includeRelated } : {}),
    page: 0,
    size: ids?.length ? ids.length : 200,
    ...(columns?.length ? { columns: columns.join(',') } : {})
  }

  // Use POST when IDs are provided to avoid gateway URL length limits
  const response = ids?.length
    ? await HTTPGatewayClient.post(
        '/poi/assets/export',
        { ids },
        {
          headers: { Accept: 'text/csv' },
          params: baseParams,
          paramsSerializer: { indexes: null },
          responseType: 'blob'
        }
      )
    : await HTTPGatewayClient.get('/poi/assets/export', {
        headers: { Accept: 'text/csv' },
        params: baseParams,
        paramsSerializer: { indexes: null },
        responseType: 'blob'
      })

  const blob = new Blob([response.data as BlobPart], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = options?.filename ?? 'export.csv'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Create a single service object.
const entityService = {
  get,
  getAll,
  getReferences,
  getRelations,
  getRelationCounts,
  searchByParams,
  clearCache,
  downloadCSV
}

/**
 * Vue composable to provide access to the singleton entity service.
 */
export const useEntityService = defineStore('entity-service', () => {
  useEventListener('entity:created', () => {
    entityService.clearCache()
  })
  useEventListener('entity:updated', () => {
    entityService.clearCache()
  })
  useEventListener('entity:deleted', () => {
    entityService.clearCache()
  })
  return entityService
})

export const useEntity = (id: string | Ref<string | undefined>) => {
  const store = useEntityService()
  const entityId = typeof id === 'string' ? ref(id) : id
  const entity = ref<Entity>()
  const isLoading = ref<boolean>(false)

  const fetch = async () => {
    if (!entityId.value) return

    isLoading.value = true
    try {
      entity.value = await store.get(entityId.value, { includeCategories: true })
    } finally {
      isLoading.value = false
    }
  }

  watch(entityId, fetch, { immediate: true })
  useEventListener('entity:created', fetch)
  useEventListener('entity:updated', fetch)
  useEventListener('entity:deleted', fetch)
  return { entity, isLoading }
}
